import React, { useState, useCallback } from "react";
import type { ResultItem } from "./ResultSection";

interface CounselFormProps {
  resultSummary: string;
  resultItems: ResultItem[];
}

export default function CounselForm({ resultSummary, resultItems }: CounselFormProps) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [includeResult, setIncludeResult] = useState(false);
  const [agree, setAgree] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!name.trim() || !phone.trim() || !agree) return;

      setStatus("submitting");
      setErrorMsg("");

      const resultText = includeResult
        ? resultItems
            .map((i) => `${i.label}: ${i.message}`)
            .join("\n")
        : "";

      const hp = honeypot.trim();
      if (hp !== "") {
        setStatus("success");
        return;
      }

      const payload = new URLSearchParams();
      payload.append("source", "자가진단");
      payload.append("유입URL", window.location.href);
      payload.append("자가진단결과", resultSummary);
      payload.append("message", includeResult ? resultText : "");
      payload.append("name", name.trim());
      payload.append("phone", phone.trim());
      payload.append("개인정보_수집이용_동의", "동의");

      try {
        const response = await fetch("/api/apply", {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: payload.toString(),
        });

        const responseText = await response.text();
        let parsed: any = null;
        let isJson = true;
        try {
          parsed = JSON.parse(responseText);
        } catch {
          parsed = null;
          isJson = false;
        }

        if (!response.ok) {
          setStatus("error");
          setErrorMsg(
            parsed?.meta?.message ||
              parsed?.message ||
              `서버 오류가 발생했습니다. (${response.status})`
          );
          return;
        }

        if (!isJson || parsed === null) {
          setStatus("error");
          setErrorMsg(
            "서버 응답을 처리할 수 없습니다. 현재 서브도메인에서는 상담 접수가 지원되지 않습니다."
          );
          return;
        }

        if (parsed.code !== "OK") {
          setStatus("error");
          setErrorMsg(
            parsed?.meta?.message ||
              parsed?.message ||
              parsed?.meta?.detail ||
              "신청 처리 중 오류가 발생했습니다."
          );
          return;
        }

        setStatus("success");
      } catch (err) {
        setStatus("error");
        setErrorMsg("네트워크 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.");
      }
    },
    [name, phone, agree, includeResult, resultSummary, resultItems]
  );

  if (status === "success") {
    return (
      <div className="bg-brand-50 border border-brand-200 rounded-lg p-6 md:p-8 text-center dark:border-brand-800 dark:bg-brand-900/20">
        <h2 className="text-xl font-bold text-gray-900 mb-2 dark:text-gray-100">신청이 접수됐습니다</h2>
        <p className="text-sm text-gray-700 dark:text-gray-300">확인 후 연락드리겠습니다.</p>
      </div>
    );
  }

  return (
    <div className="mt-10">
      <h2 className="text-xl font-bold text-gray-900 mb-2 dark:text-gray-100">
        확인이 필요한 부분, 같이 봐 드립니다
      </h2>
      <p className="text-sm text-gray-700 leading-relaxed mb-6 dark:text-gray-300">
        위 결과는 제도 문언에 비춘 <b>가능성</b>이고, 확정은 상담과 운영기관 확인을 거쳐야 합니다.
        막힌 항목의 해소 경로, 과정별 세부 조건, 수당 중복까지 통화 한 번으로 정리해 드립니다.
      </p>

      <form onSubmit={handleSubmit} className="space-y-5">
        <input type="hidden" name="source" value="자가진단" />
        <input type="hidden" name="유입URL" value={window.location.href} />
        <input type="hidden" name="자가진단결과" value={resultSummary} />
        <input type="hidden" name="message" value={includeResult ? resultItems.map((i) => `${i.label}: ${i.message}`).join("\n") : ""} />

        <div className="hnypt-field" aria-hidden="true">
          <label htmlFor="website_alt">Website</label>
          <input
            id="website_alt"
            name="website_alt"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            readOnly
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
          />
        </div>

        <div>
          <label htmlFor="이름" className="block text-sm font-medium text-gray-700 mb-2 dark:text-gray-300">
            성함
          </label>
          <input
            id="이름"
            name="name"
            type="text"
            autoComplete="name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
          />
        </div>

        <div>
          <label htmlFor="연락처" className="block text-sm font-medium text-gray-700 mb-2 dark:text-gray-300">
            연락처
          </label>
          <input
            id="연락처"
            name="phone"
            type="tel"
            inputMode="numeric"
            autoComplete="tel"
            placeholder="010-0000-0000"
            required
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
          />
        </div>

        <label
          className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-brand-500 ${
            includeResult ? "border-brand-600 bg-brand-50 dark:border-brand-500 dark:bg-brand-900/30" : "border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800"
          }`}
        >
          <input
            type="checkbox"
            name="결과동봉"
            checked={includeResult}
            onChange={(e) => setIncludeResult(e.target.checked)}
            className="mt-1 w-4 h-4 accent-brand-700 cursor-pointer"
          />
          <span className="flex-1">
            <b className="block text-gray-900 text-sm dark:text-gray-200">진단 결과 요약을 함께 보냅니다</b>
            <i className="block text-gray-500 text-xs not-italic mt-1 dark:text-gray-400">
              선택 사항입니다. 체크하지 않으면 성함·연락처만 전송됩니다.
            </i>
          </span>
        </label>

        <div className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-4 leading-relaxed dark:border-gray-800 dark:bg-gray-900 dark:text-gray-300">
          <strong>수집 항목</strong> 성함, 연락처(위 체크 시 진단 결과 요약)
          <br />
          <strong>이용 목적</strong> 국비 훈련 자격 상담 및 수강 안내
          <br />
          <strong>보유 기간</strong> 상담 후 1년(요청 시 즉시 파기)
          <br />
          동의를 거부하실 수 있으며, 거부 시 상담 연락이 어렵습니다.
        </div>

        <label
          className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-brand-500 ${
            agree ? "border-brand-600 bg-brand-50 dark:border-brand-500 dark:bg-brand-900/30" : "border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800"
          }`}
        >
          <input
            type="checkbox"
            name="개인정보_수집이용_동의"
            value="동의"
            checked={agree}
            onChange={(e) => setAgree(e.target.checked)}
            required
            className="mt-1 w-4 h-4 accent-brand-700 cursor-pointer"
          />
          <span className="text-sm text-gray-900 dark:text-gray-200">위 개인정보 수집·이용에 동의합니다.</span>
        </label>

        <button
          type="submit"
          disabled={status === "submitting" || !name.trim() || !phone.trim() || !agree}
          className="w-full py-3.5 px-4 rounded-lg text-sm font-bold text-white bg-brand-700 hover:bg-brand-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-brand-400 dark:focus-visible:ring-offset-gray-900 dark:disabled:bg-gray-700"
        >
          {status === "submitting" ? "전송 중..." : "상담 신청하기"}
        </button>

        {status === "error" && errorMsg && (
          <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
            {errorMsg}
          </p>
        )}
      </form>
    </div>
  );
}